<template>
  <SfList no-margin>
    <slot name="before"></slot>
    <SfListItem
      v-if="unreadCount > 0"
      item-id="inbox-unread"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="selectedCategory === UNREAD_CATEGORY"
      @item-click="setSelected(UNREAD_CATEGORY, '')"
    >
      <template #icon>
        <i class="fa fa-envelope-open" />
      </template>
      <template #default>
        {{ I18n.t('communication.inbox.your_unread_communication') }}
      </template>
      <template #right-content>
        <span
          class="
            tw-flex
            tw-items-center
            tw-justify-center
            tw-h-4
            tw-min-w-[16px]
            tw-rounded-full
            tw-px-1
            tw-leading-3
            tw-bg-sf-danger-light-1
            tw-text-sf-snow-lighter
            tw-text-xs
          "
          data-testid="inbox-unread-count"
        >
          {{ unreadCount }}
        </span>
      </template>
    </SfListItem>
    <SfListItem
      v-if="anyUnfilteredCommunications"
      item-id="inbox-all"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="selectedCategory === ALL_CATEGORY"
      @item-click="setSelected(ALL_CATEGORY, '')"
    >
      <template #default>
        {{ I18n.t('communication.inbox.all_communication') }}
      </template>
      <template #right-content>
        <UnreadIndicator v-if="unreadCount > 0" />
      </template>
    </SfListItem>
    <SfListItem
      v-if="!!groupedCommunication[COMPANY_CATEGORY]"
      item-id="inbox-overview"
      background="transparent"
      font-weight="medium"
      cornered
      :selected="clientOverviewSelected"
      @item-click="setSelected(COMPANY_CATEGORY, CLIENT_OVERVIEW_SECTION)"
    >
      <template #default>
        <span>
          {{ I18n.t('company.start') }}
        </span>
      </template>
      <template #right-content>
        <UnreadIndicator v-if="clientOverviewHasUnread" />
      </template>
    </SfListItem>
    <SfListItemGroup
      v-if="!!groupedCommunication[CLIENT_MEETING_CATEGORY]"
      item-id="client-meeting"
      :initially-expanded="true"
      cornered
      background="transparent"
      :header="I18n.t('client_meetings.title')"
    >
      <template #default>
        <SfListItem
          v-for="bucket in talkingPointBuckets"
          :key="`client-meeting-${bucket}`"
          :item-id="`client-meeting-${bucket}`"
          background="transparent"
          cornered
          :selected="
            selectedCategory === CLIENT_MEETING_CATEGORY &&
            selectedItem === bucket
          "
          @item-click="setSelected(CLIENT_MEETING_CATEGORY, bucket)"
        >
          <template #default>
            {{ I18n.t(`communication.inbox.talking_points_groups.${bucket}`) }}
          </template>
          <template #right-content>
            <UnreadIndicator v-if="talkingPointBucketUnread[bucket]" />
          </template>
        </SfListItem>
      </template>
      <template #right-content>
        <UnreadIndicator v-if="clientMeetingHasUnread" />
      </template>
    </SfListItemGroup>
    <SfListItemGroup
      v-for="sectionData in menuSections"
      :key="sectionData.categoryId"
      :initially-expanded="sectionData.expanded"
      cornered
      :item-id="sectionData.categoryId"
      background="transparent"
      :header="sectionData.categoryName"
    >
      <template #default>
        <SfListItem
          v-for="item in sectionItems[sectionData.categoryId]"
          :key="`${sectionData.categoryId}-${item.name}`"
          background="transparent"
          cornered
          :selected="
            selectedCategory === sectionData.categoryId &&
            selectedItem === item.name
          "
          :item-id="`${sectionData.categoryId}-${item.name}`"
          @item-click="setSelected(sectionData.categoryId, item.name)"
        >
          <template #default>
            {{ item.name }}
          </template>
          <template #right-content>
            <UnreadIndicator v-if="item.hasUnread" />
          </template>
        </SfListItem>
      </template>
      <template #right-content>
        <UnreadIndicator v-if="sectionData.hasUnread" />
      </template>
    </SfListItemGroup>
  </SfList>
</template>

<script setup>
import useI18n from '@app/shared/vue/composables/use-I18n'
import { computed } from 'vue'

import SfListItemGroup from '@app/shared/vue/components/sf-list/sf-list-item-group.vue'
import SfListItem from '@app/shared/vue/components/sf-list/sf-list-item.vue'
import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import UnreadIndicator from '@app/shared/vue/components/unread-indicator.vue'
import {
  ALL_CATEGORY,
  CLIENT_MEETING_CATEGORY,
  CLIENT_OVERVIEW_SECTION,
  COMPANY_CATEGORY,
  TALKING_POINT_BUCKETS,
  UNREAD_CATEGORY,
} from '@app/application/communication/screens/inbox/store/utils'
import useGroupedFilteredCommunications from '@app/application/communication/screens/inbox/composables/use-grouped-filtered-communications'

const I18n = useI18n()

const {
  anyUnfilteredCommunications,
  groupedCommunication,
  filteredUnreadNotificationCount: unreadCount,
  clientOverviewHasUnread,
  clientMeetingHasUnread,
  talkingPointBucketUnread,
  menuSections,
  sectionItems,
} = useGroupedFilteredCommunications()

const talkingPointBuckets = computed(() => {
  const buckets = groupedCommunication.value[CLIENT_MEETING_CATEGORY] || {}
  return TALKING_POINT_BUCKETS.filter((bucket) => buckets[bucket])
})

const props = defineProps({
  selectedCategory: {
    type: String,
    required: true,
  },
  selectedItem: {
    type: String,
    required: true,
  },
})

const clientOverviewSelected = computed(() => {
  return (
    props.selectedCategory === COMPANY_CATEGORY &&
    props.selectedItem === CLIENT_OVERVIEW_SECTION
  )
})

const emit = defineEmits({
  select: [String, String],
})

function setSelected(category, item) {
  emit('select', category, item)
}
</script>
